import moment from 'moment';

import { calculateTrialAverage } from '../../../../utils';
import {
  DURATION_EVENT_UNITS,
  DURATION_UNIT_LABELS,
  GOAL_TYPE_LABELS,
  GOAL_TYPES,
  RATE_INTERVAL_UNITS,
  SECONDS_PER_HOUR,
  SECONDS_PER_MINUTE,
  TP_GOAL_DATA_COLLECTION_METHODS,
  TRIAL_DATE_FORMAT,
} from './constants';

import type {
  IDashboardTargetDay,
  ITreatmentPlanGoal,
  ITrialSummary,
} from '../../../../../utils/types';
import type { PdfAggregatedTrial, PdfTargetData, PdfTrialPoint } from './types';

function getSessionDurationDivisor(trial: PdfAggregatedTrial): number {
  switch (trial.rateTimeUnit) {
    case RATE_INTERVAL_UNITS.HOUR:
      return trial.sessionDurationTotal / SECONDS_PER_HOUR;
    case RATE_INTERVAL_UNITS.MINUTE:
      return trial.sessionDurationTotal / SECONDS_PER_MINUTE;
    case RATE_INTERVAL_UNITS.SECOND:
      return trial.sessionDurationTotal;
    default:
      return 1;
  }
}

function getDurationUnitDivisor(durationTimeUnit: string): number {
  switch (durationTimeUnit) {
    case DURATION_EVENT_UNITS.HOUR:
      return SECONDS_PER_HOUR;
    case DURATION_EVENT_UNITS.MINUTE:
      return SECONDS_PER_MINUTE;
    default:
      return 1;
  }
}

export function aggregateTrials(trials: IDashboardTargetDay[]): Record<string, PdfAggregatedTrial> {
  return trials.reduce<Record<string, PdfAggregatedTrial>>((acc, trial) => {
    const date = moment(trial.date).format(TRIAL_DATE_FORMAT);
    const current = acc[date] ?? {
      date,
      instances: 0,
      success: 0,
      attempts: 0,
      count: 0,
      duration: 0,
      rateTotal: 0,
      sessionDurationTotal: 0,
      rateTimeUnit: '',
      durationEventTotal: 0,
      durationEventCount: 0,
      durationTimeUnit: '',
    };
    const durationEventTotal = trial.duration_events?.reduce((sum, dur) => sum + dur, 0) ?? 0;
    return {
      ...acc,
      [date]: {
        ...current,
        instances: current.instances + trial.instances,
        success: current.success + trial.success,
        attempts: current.attempts + trial.attempts,
        count: current.count + 1,
        rateTotal: current.rateTotal + (trial.total_trial_rate_events ?? 0),
        sessionDurationTotal: current.sessionDurationTotal + (trial.expected_duration ?? 0),
        rateTimeUnit: trial.measurement_units_interval_unit ?? '',
        durationEventTotal: current.durationEventTotal + durationEventTotal,
        durationEventCount: current.durationEventCount + (trial.duration_events?.length ?? 0),
        durationTimeUnit: trial.measurement_units_duration_unit ?? '',
        durations: trial.durations,
      },
    };
  }, {});
}

export function toTrialPoint(trial: PdfAggregatedTrial, dcm: string): PdfTrialPoint {
  const sessionDurationDivisor = getSessionDurationDivisor(trial);
  const durationUnitDivisor = getDurationUnitDivisor(trial.durationTimeUnit);
  return {
    date: trial.date,
    instances: trial.instances,
    success: trial.success,
    attempts: trial.attempts,
    durations: trial.durations,
    rate:
      sessionDurationDivisor > 0
        ? parseFloat((trial.rateTotal / sessionDurationDivisor).toFixed(2))
        : 0,
    type: dcm,
    durationEventAverage:
      trial.durationEventCount > 0
        ? parseFloat(
            (trial.durationEventTotal / durationUnitDivisor / trial.durationEventCount).toFixed(2),
          )
        : 0,
    durationEventTotal: parseFloat((trial.durationEventTotal / durationUnitDivisor).toFixed(2)),
    percentage:
      trial.attempts > 0 ? parseFloat(((trial.success / trial.attempts) * 100).toFixed(2)) : 0,
  };
}

export function transformTrialSummary(
  data: ITrialSummary[][],
  goals: ITreatmentPlanGoal[],
): PdfTargetData[] {
  return data
    .flat()
    .filter((goal) => goal && goal.targets)
    .flatMap((goal) =>
      goal.targets.map((target) => {
        const sortedTrials = [...target.trials].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );
        // The legacy report reads the units from the last trial in date order.
        const lastTrial = sortedTrials[sortedTrials.length - 1];
        const dcm: string = lastTrial?.data_collection_method ?? '';
        const rateUnit = lastTrial?.measurement_units_interval_unit ?? '';
        const durationDCMEventUnit = lastTrial?.measurement_units_duration_unit ?? '';
        const trials = Object.values(aggregateTrials(sortedTrials)).map((trial) =>
          toTrialPoint(trial, dcm),
        );
        const goalType: string = goals.find((g) => g.id === goal.goal_id)?.goal_type || '';
        const averageResult = calculateTrialAverage(trials);
        return {
          goalId: goal.goal_id,
          goalLabel: goal.label,
          targetId: target.target_id,
          targetLabel: target.label,
          goalType,
          trials,
          dcm,
          rateUnit,
          durationDCMEventUnit,
          average: averageResult.value,
          averageLabel: averageResult.label,
          durationUnit: target.event_duration_measurement_unit,
        };
      }),
    );
}

export function getDurationUnitLabel(durationDCMEventUnit: string): string {
  if (durationDCMEventUnit === DURATION_EVENT_UNITS.HOUR) {
    return DURATION_UNIT_LABELS[DURATION_EVENT_UNITS.HOUR];
  }
  if (durationDCMEventUnit === DURATION_EVENT_UNITS.MINUTE) {
    return DURATION_UNIT_LABELS[DURATION_EVENT_UNITS.MINUTE];
  }
  if (durationDCMEventUnit === DURATION_EVENT_UNITS.SECOND) {
    return DURATION_UNIT_LABELS[DURATION_EVENT_UNITS.SECOND];
  }
  return '';
}

export function getGoalTypeLabel(goalType: string): string {
  if (goalType === GOAL_TYPES.BEHAVIOR_REDUCTION) {
    return GOAL_TYPE_LABELS[GOAL_TYPES.BEHAVIOR_REDUCTION];
  }
  if (goalType === GOAL_TYPES.SKILL_ACQUISITION) {
    return GOAL_TYPE_LABELS[GOAL_TYPES.SKILL_ACQUISITION];
  }
  if (goalType === GOAL_TYPES.CAREGIVER) {
    return GOAL_TYPE_LABELS[GOAL_TYPES.CAREGIVER];
  }
  return '';
}

export function getAverageUnitLabel(dcm: string, durationUnitLabel: string): string {
  if (dcm === TP_GOAL_DATA_COLLECTION_METHODS.PERCENTAGE) return '%';
  if (dcm === TP_GOAL_DATA_COLLECTION_METHODS.DURATION) return durationUnitLabel;
  if (dcm === TP_GOAL_DATA_COLLECTION_METHODS.RATE) return '';
  return 'instances';
}

export function getChartUnitSuffix(
  dcm: string,
  durationDCMEventUnit: string,
  rateUnit: string,
): string {
  if (dcm === TP_GOAL_DATA_COLLECTION_METHODS.DURATION) {
    return ` in ${durationDCMEventUnit.toLowerCase()}s`;
  }
  if (dcm === TP_GOAL_DATA_COLLECTION_METHODS.RATE) {
    return ` per ${rateUnit.substring(0, rateUnit.length - 1)}`;
  }
  return '';
}
